import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SignUpButton, useAuth } from "@clerk/react";
import "./PcaPricing.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
const CTA_LABEL = "Claim Lifetime Porsche Access";

const monthlyFeatures = [
  "Codie – AI Coding Assistant",
  "Access to Deep Research",
  "5+ Starter Kits",
  "5,000 Monthly Credits",
  "Create All Dev Docs: PRDs, Tech stacks, etc.",
  "Refine Project Ideas with AI",
  "Work With All Advanced Coding Tools",
  "Access 40+ Ready-to-Use Templates",
  "Access to 01-preview Model & Claude",
  "Complete 15 Projects per Month",
];

const yearlyFeatures = [
  "Exclusive discounts and free plans on AI tools",
  "Codie – AI Coding Assistant",
  "Access to Deep Research",
  "5+ Starter Kits",
  "30-minute one-on-one call",
  "6,000 Monthly Credits",
  "Complete 18 Projects per month",
  "Priority Support",
  "Early Access to New Features",
  "Create All Dev Docs: PRDs, Tech stacks, etc.",
  "Refine Project Ideas with AI",
  "Work With All Advanced Coding Tools",
  "Access 40+ Ready-to-Use Templates",
  "Access to 01-preview Model & Claude",
  "Get Dedicated Support and Community",
];

const compareRows = [
  { years: "1 year", annual: "$396" },
  { years: "3 years", annual: "$1,188" },
  { years: "5 years", annual: "$1,980" },
  { years: "10 years", annual: "$3,960" },
];

function PcaPricing() {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isRedirecting, setIsRedirecting] = React.useState(false);
  const [error, setError] = React.useState(null);
  const inFlight = React.useRef(false);

  const startCheckout = React.useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setIsRedirecting(true);
    setError(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/api/pca/checkout`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "checkout_failed");
      if (data.alreadyPurchased) {
        navigate("/profile");
        return;
      }
      window.location.href = data.url;
    } catch (err) {
      console.error("PCA checkout failed", err);
      inFlight.current = false;
      setIsRedirecting(false);
      setError("We couldn't open the secure checkout. Please try again.");
    }
  }, [getToken, navigate]);

  React.useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    if (searchParams.get("checkout") !== "start") return;
    const next = new URLSearchParams(searchParams);
    next.delete("checkout");
    setSearchParams(next, { replace: true });
    startCheckout();
  }, [isLoaded, isSignedIn, searchParams, setSearchParams, startCheckout]);

  return (
    <section className="pricing pca-pricing" id="pricing">
      <div className="pricing__badge">Pricing</div>
      <div className="pca-pricing__header">
        <div className="pca-pricing__eyebrow">Exclusive to PCA Members</div>
        <h2 className="pca-pricing__headline">One payment. Lifetime Porsche Access.</h2>
      </div>
      <div className="pricing__cards">
        <div className="pricing-card pricing-card--annual">
          <div className="pricing-card__price-row">
            <span className="pricing-card__price">$396</span>
            <span className="pricing-card__period">once</span>
          </div>
          <p className="pricing-card__compare">
            Everyone else: <span className="pricing-card__strike">$396 every year</span>
          </p>

          <table className="pca-pricing__table">
            <thead>
              <tr>
                <th>Years of Access</th>
                <th>Standard Annual Plan</th>
                <th>PCA Lifetime</th>
              </tr>
            </thead>
            <tbody>
              {compareRows.map((row) => (
                <tr key={row.years}>
                  <td>{row.years}</td>
                  <td>{row.annual}</td>
                  <td className="pca-pricing__table-lifetime">$396</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="pca-pricing__table-note">
            Most users save more in a single auction than they'll spend in a decade of access. For PCA members, a decade costs $396.
          </p>

          <ul className="pricing-card__feature-grid">
            <li><span className="pricing-card__check">✓</span> Bidder iQ Advanced Analytics</li>
            <li><span className="pricing-card__check">✓</span> Bidder iQ Chrome Extension</li>
            <li><span className="pricing-card__check">✓</span> Price Guide with real comps</li>
            <li><span className="pricing-card__check">✓</span> Market trend analysis</li>
            <li><span className="pricing-card__check">✓</span> Save your favorite searches</li>
            <li><span className="pricing-card__check">✓</span> Sold &amp; unsold auction results</li>
          </ul>

          {isLoaded && !isSignedIn ? (
            <SignUpButton
              mode="modal"
              forceRedirectUrl="/pca?checkout=start"
              signInForceRedirectUrl="/pca?checkout=start"
            >
              <button type="button" className="pricing-card__main-cta">{CTA_LABEL}</button>
            </SignUpButton>
          ) : (
            <button
              type="button"
              className="pricing-card__main-cta"
              onClick={startCheckout}
              disabled={!isLoaded || isRedirecting}
            >
              {isRedirecting ? "Opening secure checkout…" : CTA_LABEL}
            </button>
          )}
          {error && <div className="pricing-card__cta-error">{error}</div>}

          <ul className="pricing-card__benefits">
            <li><span className="pricing-card__check">✓</span> Instant access</li>
            <li><span className="pricing-card__check">✓</span> Secure Checkout by Stripe</li>
            <li><span className="pricing-card__check">✓</span> 100% Satisfaction Guarantee</li>
          </ul>
        </div>
      </div>
    </section>
  );
}

export default PcaPricing;
